import { Button, Icon, Radio } from 'semantic-ui-react';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { useContext, useEffect } from 'react';
import { Text } from 'components/typography';
import { useGetUserIn2n } from 'services/2n/get-user';
import { useCreateUserIn2n } from 'services/2n/create-user';
import { useUpdateUserIn2n } from 'services/2n/update-user';
import { useModal } from 'store/modals';
import { useFormContext } from 'react-hook-form';
import styles from '../../styles.module.scss';
import { UserAdministrationModalContext } from '../../../../../../UserAdministrationModalContext';

export const Manage2nPermissions = () => {
  const intl = useIntl();
  const { selectedUser, setService } = useContext(UserAdministrationModalContext);
  const getUserIn2N = useGetUserIn2n(selectedUser?.email);
  const createUserIn2N = useCreateUserIn2n(selectedUser);
  const updateUserIn2N = useUpdateUserIn2n(getUserIn2N.user);
  const hasAssignedGroups = getUserIn2N.user?.assignedGroups?.length > 0;
  const isSetupRequiredFor2N = !getUserIn2N.user || !hasAssignedGroups;
  const hasAccess = !!getUserIn2N.user && !getUserIn2N.isSuspended;
  const [hasUserAccess, setHasUserAccess] = React.useState(false);

  const { openModal, closeModal } = useModal();

  useEffect(() => {
    setHasUserAccess(hasAccess);
  }, [hasAccess]);

  const { formState: { dirtyFields }, reset } = useFormContext();
  const showDiscardModal = dirtyFields.firstName || dirtyFields.lastName;

  const handleToggle = async (event, { checked }) => {
    setHasUserAccess(checked);

    if (!getUserIn2N.user && checked) {
      if (showDiscardModal) {
        openModal({
          modalType: 'ConfirmationModal',
          modalProps: {
            type: 'error',
            title: intl.formatMessage({
              id: 'unsavedChanges',
            }),
            content: intl.formatMessage({
              id: 'unsavedChangesDescription',
            }),
            confirmLabel: intl.formatMessage({
              id: 'discard',
            }),
            cancelLabel: intl.formatMessage({
              id: 'stay',
            }),
            onConfirm: async () => {
              reset({
                firstName: selectedUser.firstName || '',
                lastName: selectedUser.lastName || '',
                email: selectedUser.email || '',
                phone: selectedUser.phone || '',
                mobilephone: selectedUser.mobilephone || '',
                workphone: selectedUser.workphone || '',
                objectId: selectedUser.objectId || '',
                address: selectedUser.address || '',
                addressId: selectedUser.addressId || '',
                postalCode: selectedUser.postalCode || '',
              });
              closeModal();
            },
            onCancel: async () => {
              closeModal();
            },
          },
        });
      } else {
        await createUserIn2N.mutateAsync(selectedUser);
      }
    }

    if (getUserIn2N.user && checked) {
      updateUserIn2N.mutate({ isSuspended: false });
    } else if (getUserIn2N.user && !checked) {
      openModal({
        modalType: 'ConfirmationModal',
        modalProps: {
          title: intl.formatMessage({ id: 'updatePermissionForUserIn2n' }),
          content: intl.formatMessage({ id: 'updatePermissionForUserIn2nContent' }),
          onConfirm: () => {
            updateUserIn2N.mutate({ isSuspended: true });
          },
          onCancel: () => {
            setHasUserAccess(hasAccess);
          },
        },
      });
    }
  };

  return (
    <div className={styles.service}>
      <Radio
        label={intl.formatMessage({ id: 'accessControl2n' })}
        toggle
        name="accessControl2n"
        checked={hasUserAccess}
        disabled={getUserIn2N.isLoading || updateUserIn2N.isLoading || createUserIn2N.isLoading}
        onChange={handleToggle}
      />

      {hasUserAccess && (
        <div className={styles.configureService}>
          <Button
            basic
            icon
            labelPosition="right"
            disabled={getUserIn2N.isLoading || updateUserIn2N.isLoading || createUserIn2N.isLoading}
            color="primary"
            onClick={() => setService('2N Access Control')}
          >
            <FormattedMessage id="configure" />
            <Icon name="pencil alternate" />
          </Button>

          {isSetupRequiredFor2N && (
            <Text red>
              <FormattedMessage id="setupRequired" />
            </Text>
          )}
        </div>
      )}
    </div>
  );
};
